/**
 * API Caching Utility for NexusDialer
 *
 * Provides a centralized caching system for API responses to improve performance.
 * Features:
 * - TTL (Time-To-Live) based caching
 * - Cache invalidation by key or pattern
 * - Cache size limits
 * - Forced refresh capability
 * - Cache hit/miss tracking
 */

// Cache configuration defaults
const DEFAULT_CONFIG = {
  defaultTTL: 5 * 60 * 1000, // 5 minutes in milliseconds
  maxCacheSize: 100, // Maximum number of entries in cache
  debug: import.meta.env.MODE === "development",
};

class ApiCache {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.cache = new Map();
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      invalidations: 0,
    };

    // Initialize periodic cleanup
    if (typeof window !== "undefined") {
      this.cleanupInterval = setInterval(() => this.cleanup(), 30 * 60 * 1000); // Clean up every 30 minutes
    }
  }

  /**
   * Generate a cache key based on URL and params
   *
   * @param {string} url - API endpoint URL
   * @param {Object} params - Query parameters
   * @returns {string} - Cache key
   */
  generateKey(url, params = {}) {
    if (!url) return null;

    // Sort params to ensure consistent keys regardless of param order
    const sortedParams = params
      ? Object.entries(params).sort(([a], [b]) => a.localeCompare(b))
      : [];
    const paramsString = sortedParams.length
      ? `?${sortedParams.map(([key, value]) => `${key}=${JSON.stringify(value)}`).join("&")}`
      : "";

    return `${url}${paramsString}`;
  }

  /**
   * Get a value from cache
   *
   * @param {string} key - Cache key
   * @returns {any|null} - Cached value or null if not found/expired
   */
  get(key) {
    if (!key || !this.cache.has(key)) {
      this.stats.misses++;
      this.logDebug(`Cache miss: ${key}`);
      return null;
    }

    const cached = this.cache.get(key);

    // Check if the cache entry has expired
    if (cached.expiry && cached.expiry < Date.now()) {
      this.cache.delete(key);
      this.stats.misses++;
      this.logDebug(`Cache expired: ${key}`);
      return null;
    }

    this.stats.hits++;
    this.logDebug(`Cache hit: ${key}`);
    return cached.data;
  }

  /**
   * Set a value in cache
   *
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds (optional, uses default if not specified)
   */
  set(key, data, ttl) {
    if (!key) return;

    const expiry =
      ttl !== undefined
        ? Date.now() + ttl
        : this.config.defaultTTL
          ? Date.now() + this.config.defaultTTL
          : null;

    this.cache.set(key, {
      data,
      expiry,
      createdAt: Date.now(),
    });

    this.stats.sets++;
    this.logDebug(`Cache set: ${key}, TTL: ${ttl || this.config.defaultTTL}ms`);

    // Enforce cache size limit
    this.enforceCacheSizeLimit();
  }

  /**
   * Invalidate cache entries
   *
   * @param {string|RegExp} keyOrPattern - Specific key or pattern to match keys against
   * @returns {number} - Number of invalidated cache entries
   */
  invalidate(keyOrPattern) {
    if (!keyOrPattern) {
      return 0;
    }

    let count = 0;

    if (typeof keyOrPattern === "string") {
      // Invalidate specific key
      if (this.cache.has(keyOrPattern)) {
        this.cache.delete(keyOrPattern);
        count = 1;
      }
    } else if (keyOrPattern instanceof RegExp) {
      // Invalidate by pattern
      for (const key of this.cache.keys()) {
        if (keyOrPattern.test(key)) {
          this.cache.delete(key);
          count++;
        }
      }
    }

    this.stats.invalidations += count;
    this.logDebug(`Invalidated ${count} cache entries`);
    return count;
  }

  /**
   * Invalidate cache entries by URL pattern
   *
   * @param {string} urlPattern - URL or URL part to match
   * @returns {number} - Number of invalidated cache entries
   */
  invalidateByUrl(urlPattern) {
    if (!urlPattern) return 0;

    return this.invalidate(
      new RegExp(`^${urlPattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`),
    );
  }

  /**
   * Clear the entire cache
   */
  clear() {
    const count = this.cache.size;
    this.cache.clear();
    this.stats.invalidations += count;
    this.logDebug(`Cleared entire cache (${count} entries)`);
  }

  /**
   * Get cache statistics
   *
   * @returns {Object} - Cache stats
   */
  getStats() {
    return {
      ...this.stats,
      cacheSize: this.cache.size,
      hitRate: this.stats.hits / (this.stats.hits + this.stats.misses) || 0,
    };
  }

  /**
   * Clean up expired cache entries
   *
   * @returns {number} - Number of entries removed
   */
  cleanup() {
    const now = Date.now();
    let count = 0;

    for (const [key, value] of this.cache.entries()) {
      if (value.expiry && value.expiry < now) {
        this.cache.delete(key);
        count++;
      }
    }

    if (count > 0) {
      this.logDebug(`Cleanup removed ${count} expired cache entries`);
    }

    return count;
  }

  /**
   * Enforce the maximum cache size by removing oldest entries
   */
  enforceCacheSizeLimit() {
    if (
      !this.config.maxCacheSize ||
      this.cache.size <= this.config.maxCacheSize
    ) {
      return;
    }

    // Sort entries by creation time (oldest first)
    const entries = Array.from(this.cache.entries()).sort(
      ([, a], [, b]) => a.createdAt - b.createdAt,
    );

    // Calculate how many entries to remove
    const removeCount = this.cache.size - this.config.maxCacheSize;

    if (removeCount <= 0) return;

    // Remove oldest entries
    for (let i = 0; i < removeCount; i++) {
      this.cache.delete(entries[i][0]);
    }

    this.logDebug(
      `Removed ${removeCount} oldest cache entries due to size limit`,
    );
  }

  /**
   * Log debug information if debug mode is enabled
   *
   * @param {string} message - Debug message
   */
  logDebug(message) {
    if (this.config.debug) {
    }
  }

  /**
   * Clean up resources when no longer needed
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.cache.clear();
  }
}

// Create and export singleton instance
const apiCache = new ApiCache();

export default apiCache;

// Helper functions for common use cases

/**
 * Wrap an API fetch function with caching logic
 *
 * @param {Function} fetchFn - Original fetch function
 * @param {Object} options - Cache options
 * @param {number} options.ttl - Time to live in milliseconds
 * @param {string} options.cacheKey - Custom cache key (optional)
 * @param {boolean} options.forceRefresh - Force a refresh regardless of cache (optional)
 * @returns {Promise<any>} - Promise resolving to data
 */
export const withCache = async (fetchFn, options = {}) => {
  const { ttl, cacheKey, forceRefresh } = options;

  if (!cacheKey) {
    throw new Error("Cache key is required for withCache");
  }

  // Check cache first (unless force refresh is requested)
  if (!forceRefresh) {
    const cachedData = apiCache.get(cacheKey);
    if (cachedData !== null) {
      return cachedData;
    }
  }

  // Fetch fresh data
  const data = await fetchFn();

  // Store in cache
  apiCache.set(cacheKey, data, ttl);

  return data;
};

/**
 * Create a cached version of an API service method
 *
 * @param {Function} originalMethod - Original API method
 * @param {Function} keyGenerator - Function to generate cache key from arguments
 * @param {Object} options - Cache options
 * @param {number} options.ttl - Cache TTL in ms
 * @returns {Function} - Cached API method
 */
export const createCachedMethod = (
  originalMethod,
  keyGenerator,
  options = {},
) => {
  return async (...args) => {
    const cacheKey = keyGenerator(...args);
    const forceRefresh = args.some(
      (arg) => arg && typeof arg === "object" && arg.forceRefresh === true,
    );

    return withCache(() => originalMethod(...args), {
      cacheKey,
      ttl: options.ttl || undefined,
      forceRefresh,
    });
  };
};
